/**
 * Workflow canvas <-> JSON serializer (SPEC PX-4 — workflow-builder-2026-06-17).
 *
 * The single most load-bearing module in the spec: it compiles the canvas
 * node/edge graph DOWN to the engine's EXACT stored `workflow_definition` shape,
 * and decompiles a stored definition back UP to a node/edge graph. The round-trip
 * is LOSSLESS (no engine fork) — a definition authored via Firestore/API and
 * edited on the canvas re-serializes to the same actions / jump-index shape the
 * engine already validates and executes.
 *
 * Engine storage contract (verified against `services/workflow_engine.py`):
 *   - `actions` is a FLAT array indexed by position; config keys are FLAT on each
 *     action dict — `{type:'wait', delay_minutes:30}`, NEVER `{type:'wait',
 *     config:{...}}`. Nesting would NOT parse.
 *   - Control flow = INTEGER indices into the same `actions` array:
 *       linear:        action[i] -> action[i+1]
 *       branch:        branches[].target_action_index + default_target_action_index
 *       split:         split_branches[].{weight, target_action_index}
 *       wait_for_event:resume_target_action_index + timeout_target_action_index
 *       wait:          {type:'wait', delay_minutes} — linear; defers the next hop.
 *   - trigger = {type, config}; re_enrollment = {policy}; goal_condition optional.
 *
 * Design: each action node has a stable string `id`. Inside a node's `config`,
 * control-flow targets are stored as NODE-ID references (string) under the
 * `*_node_id` keys (e.g. `branches[].target_node_id`). At the storage boundary
 * `graphToDefinition` resolves each node-id to that node's INTEGER array index;
 * `definitionToGraph` does the inverse. The flat array order is the node order in
 * `graph.nodes` (the trigger node excluded), so a decompile->compile is index-
 * preserving and lossless.
 */

import type {
  GraphEdge,
  GraphNode,
  WorkflowDefinition,
  WorkflowGraph,
  WorkflowActionDict,
  WorkflowTrigger,
  ReEnrollment,
  GoalCondition,
} from '@/types/workflow';
import { TERMINAL_NODE_ID, TRIGGER_NODE_ID } from '@/types/workflow';

// Layout constants (vertical flow; branch fan-out spreads horizontally).
const NODE_X = 40;
const NODE_Y_START = 40;
const NODE_Y_STEP = 120;

/** The node id for the action stored at array index `i`. */
export function actionNodeId(index: number): string {
  return `action-${index}`;
}

/** Parse an `action-<i>` node id back to its integer index, or null. */
function nodeIdToIndex(nodeId: string | null | undefined): number | null {
  if (!nodeId || !nodeId.startsWith('action-')) return null;
  const n = Number(nodeId.slice('action-'.length));
  return Number.isInteger(n) ? n : null;
}

// ---------------------------------------------------------------------------
// Decompile: stored definition -> node/edge graph (R1)
// ---------------------------------------------------------------------------

interface BranchEntry {
  condition?: Record<string, unknown>;
  target_action_index?: number;
  target_node_id?: string;
}
interface SplitEntry {
  weight?: number;
  target_action_index?: number;
  target_node_id?: string;
}

/** Short human summary of a branch condition for an edge label. */
function conditionSummary(cond: Record<string, unknown> | undefined): string {
  if (!cond || typeof cond !== 'object') return 'if';
  const t = cond.type as string | undefined;
  if (t === 'tag_present') return `if tag ${cond.tag ?? cond.tag_id ?? ''}`.trim();
  if (t === 'field_equals') return `if ${cond.field} = ${cond.value}`;
  if (t === 'field_gt') return `if ${cond.field} > ${cond.value}`;
  if (t === 'field_lt') return `if ${cond.field} < ${cond.value}`;
  if (t === 'field_gte') return `if ${cond.field} >= ${cond.value}`;
  if (t === 'field_lte') return `if ${cond.field} <= ${cond.value}`;
  if (t === 'trigger_payload_equals') return `if ${cond.key} = ${cond.value}`;
  if (t === 'AND') return 'if all';
  if (t === 'OR') return 'if any';
  return t ? `if ${t}` : 'if';
}

/**
 * Decompile a stored `workflow_definition` into a node/edge graph.
 *
 * - exactly one `trigger` node (entry), carrying trigger.type + config;
 * - one node per action, carrying its `type` + the FLAT config fields;
 * - edges for control flow: linear, branch (+ default), split, event, timeout.
 * - an out-of-bounds target -> an edge to the TERMINAL marker (no throw).
 */
export function definitionToGraph(def: Partial<WorkflowDefinition>): WorkflowGraph {
  const actions: WorkflowActionDict[] = Array.isArray(def.actions) ? def.actions : [];
  const trigger: WorkflowTrigger = def.trigger || { type: 'new_lead', config: {} };
  const reEnroll: ReEnrollment = def.re_enrollment || { policy: 'once' };

  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];

  // Trigger node (entry).
  nodes.push({
    id: TRIGGER_NODE_ID,
    kind: 'trigger',
    type: trigger.type,
    config: { ...(trigger.config || {}) },
    x: NODE_X,
    y: NODE_Y_START,
  });

  const n = actions.length;
  const inBounds = (i: number | undefined | null): i is number =>
    typeof i === 'number' && Number.isInteger(i) && i >= 0 && i < n;

  // Map an integer target index -> a node id (TERMINAL when out of bounds).
  const targetNode = (i: number | undefined | null): string =>
    inBounds(i) ? actionNodeId(i) : TERMINAL_NODE_ID;

  // Action nodes.
  actions.forEach((raw, i) => {
    const { type, ...rest } = raw;
    // Convert any stored integer targets into node-id references on the node
    // config so the in-memory model is index-free (re-numbered losslessly on
    // compile). Keep the original keys for non-control config.
    const config: Record<string, unknown> = { ...rest };

    if (type === 'branch') {
      const branches = (Array.isArray(rest.branches) ? rest.branches : []) as BranchEntry[];
      config.branches = branches.map((b) => ({
        condition: b.condition || {},
        target_node_id: targetNode(b.target_action_index),
      }));
      const split = (Array.isArray(rest.split_branches) ? rest.split_branches : []) as SplitEntry[];
      config.split_branches = split.map((s) => ({
        weight: s.weight ?? 1,
        target_node_id: targetNode(s.target_action_index),
      }));
      if (rest.default_target_action_index !== undefined && rest.default_target_action_index !== null) {
        config.default_target_node_id = targetNode(rest.default_target_action_index as number);
        delete config.default_target_action_index;
      }
      delete config.target_action_index;
    } else if (type === 'wait_for_event') {
      // resume defaults to i+1; timeout defaults to i+1 too (engine default).
      const resume = rest.resume_target_action_index;
      const timeout = rest.timeout_target_action_index;
      config.resume_target_node_id = targetNode(
        resume === undefined || resume === null ? i + 1 : (resume as number)
      );
      config.timeout_target_node_id = targetNode(
        timeout === undefined || timeout === null ? i + 1 : (timeout as number)
      );
      delete config.resume_target_action_index;
      delete config.timeout_target_action_index;
    }

    nodes.push({
      id: actionNodeId(i),
      kind: 'action',
      type,
      config,
      x: NODE_X,
      y: NODE_Y_START + (i + 1) * NODE_Y_STEP,
    });

    // Edges out of this action.
    if (type === 'branch') {
      const branches = (config.branches as BranchEntry[]) || [];
      const split = (config.split_branches as SplitEntry[]) || [];
      if (split.length) {
        split.forEach((s, bi) => {
          edges.push({
            id: `${actionNodeId(i)}-split-${bi}`,
            source: actionNodeId(i),
            target: s.target_node_id || TERMINAL_NODE_ID,
            kind: 'split',
            label: `${s.weight ?? 1}`,
          });
        });
      } else {
        branches.forEach((b, bi) => {
          edges.push({
            id: `${actionNodeId(i)}-branch-${bi}`,
            source: actionNodeId(i),
            target: b.target_node_id || TERMINAL_NODE_ID,
            kind: 'branch',
            label: conditionSummary(b.condition),
          });
        });
        if (config.default_target_node_id) {
          edges.push({
            id: `${actionNodeId(i)}-default`,
            source: actionNodeId(i),
            target: config.default_target_node_id as string,
            kind: 'branch_default',
            label: 'else',
          });
        }
      }
    } else if (type === 'wait_for_event') {
      edges.push({
        id: `${actionNodeId(i)}-event`,
        source: actionNodeId(i),
        target: (config.resume_target_node_id as string) || TERMINAL_NODE_ID,
        kind: 'event',
        label: 'event',
      });
      edges.push({
        id: `${actionNodeId(i)}-timeout`,
        source: actionNodeId(i),
        target: (config.timeout_target_node_id as string) || TERMINAL_NODE_ID,
        kind: 'timeout',
        label: 'timeout',
      });
    } else {
      // Linear: action[i] -> action[i+1] (or terminal at the end).
      edges.push({
        id: `${actionNodeId(i)}-next`,
        source: actionNodeId(i),
        target: i + 1 < n ? actionNodeId(i + 1) : TERMINAL_NODE_ID,
        kind: 'linear',
        label: type === 'wait' ? `${(config.delay_minutes as number) ?? 0} min` : undefined,
      });
    }
  });

  // Trigger -> first action (or terminal if empty).
  edges.unshift({
    id: 'trigger-next',
    source: TRIGGER_NODE_ID,
    target: n > 0 ? actionNodeId(0) : TERMINAL_NODE_ID,
    kind: 'linear',
  });

  return {
    nodes,
    edges,
    name: def.name || '',
    trigger: { type: trigger.type, config: { ...(trigger.config || {}) } },
    re_enrollment: reEnroll,
    goal_condition: def.goal_condition ?? null,
  };
}

// ---------------------------------------------------------------------------
// Compile: node/edge graph -> stored definition (R2)
// ---------------------------------------------------------------------------

interface CompiledDefinition {
  name: string;
  trigger: WorkflowTrigger;
  actions: WorkflowActionDict[];
  re_enrollment: ReEnrollment;
  goal_condition?: GoalCondition | null;
}

/**
 * Compile the node graph DOWN to the engine's flat `actions[]` + integer
 * jump-index shape — the EXACT inverse of `definitionToGraph`.
 *
 * - `actions` is a FLAT array; config keys are written at the TOP LEVEL of each
 *   action dict (NEVER under a `config` key).
 * - every control-flow target compiles to an INTEGER `target_action_index` that
 *   is a valid position in the emitted array (a node-id that resolves to a
 *   removed/terminal node -> a sentinel index === actions.length, which the
 *   engine treats as a clean terminal completion, never out-of-range-by-more).
 *
 * The action node ORDER in `graph.nodes` IS the array order (a deterministic
 * topological flatten — decompile preserved storage order, structural edits keep
 * the order stable), so decompile->compile renumbers nothing and is lossless.
 */
export function graphToDefinition(graph: WorkflowGraph): CompiledDefinition {
  const actionNodes = graph.nodes.filter((nd) => nd.kind === 'action');

  // node id -> emitted array index.
  const indexOf = new Map<string, number>();
  actionNodes.forEach((nd, i) => indexOf.set(nd.id, i));

  const len = actionNodes.length;
  // Resolve a node-id reference to an in-bounds integer index. A reference to a
  // node that no longer exists (deleted) or the terminal marker compiles to
  // `len` — the engine's runner treats an index === len as a clean terminal
  // (next_index >= len -> completed), so no jump ever dangles out-of-range.
  const resolve = (nodeId: string | undefined | null): number => {
    if (nodeId && indexOf.has(nodeId)) return indexOf.get(nodeId) as number;
    return len; // terminal
  };

  const actions: WorkflowActionDict[] = actionNodes.map((nd) => {
    const cfg = nd.config || {};
    const action: WorkflowActionDict = { type: nd.type };

    if (nd.type === 'branch') {
      const branches = (Array.isArray(cfg.branches) ? cfg.branches : []) as BranchEntry[];
      const split = (Array.isArray(cfg.split_branches) ? cfg.split_branches : []) as SplitEntry[];
      if (split.length) {
        action.split_branches = split.map((s) => ({
          weight: s.weight ?? 1,
          target_action_index: resolve(s.target_node_id),
        }));
      } else {
        action.branches = branches.map((b) => ({
          condition: b.condition || {},
          target_action_index: resolve(b.target_node_id),
        }));
        if (cfg.default_target_node_id !== undefined && cfg.default_target_node_id !== null) {
          action.default_target_action_index = resolve(cfg.default_target_node_id as string);
        }
      }
    } else if (nd.type === 'wait_for_event') {
      // event_type / timeout_minutes / tag_filter / listener_ttl_days pass
      // through flat (preserve listener_ttl_days if present — runtime-honored).
      for (const [k, v] of Object.entries(cfg)) {
        if (
          k === 'resume_target_node_id' ||
          k === 'timeout_target_node_id' ||
          k === 'resume_target_action_index' ||
          k === 'timeout_target_action_index'
        ) {
          continue;
        }
        action[k] = v;
      }
      action.resume_target_action_index = resolve(cfg.resume_target_node_id as string);
      action.timeout_target_action_index = resolve(cfg.timeout_target_node_id as string);
    } else {
      // Linear / leaf action: copy flat config verbatim, stripping any stray
      // control-flow node-id keys (only branch/wait_for_event carry those).
      for (const [k, v] of Object.entries(cfg)) {
        if (k.endsWith('_node_id')) continue;
        action[k] = v;
      }
    }
    return action;
  });

  const reEnroll: ReEnrollment = graph.re_enrollment || { policy: 'once' };
  const trigger: WorkflowTrigger = {
    type: graph.trigger.type,
    config: { ...(graph.trigger.config || {}) },
  };

  const out: CompiledDefinition = {
    name: graph.name,
    trigger,
    actions,
    re_enrollment: reEnroll,
  };
  if (graph.goal_condition) out.goal_condition = graph.goal_condition;
  return out;
}

// ---------------------------------------------------------------------------
// Structural edit helpers (R2) — array-position math, NOT drag.
// ---------------------------------------------------------------------------

/** Re-id action nodes to `action-<position>` after a structural edit, rewriting
 * every control-flow node-id reference so nothing dangles. Returns a new graph. */
export function renumberGraph(graph: WorkflowGraph): WorkflowGraph {
  const actionNodes = graph.nodes.filter((nd) => nd.kind === 'action');
  // old id -> new id (position-based).
  const remap = new Map<string, string>();
  actionNodes.forEach((nd, i) => remap.set(nd.id, actionNodeId(i)));
  const newId = (oldId: string | undefined | null): string | undefined => {
    if (!oldId) return undefined;
    if (oldId === TERMINAL_NODE_ID) return TERMINAL_NODE_ID;
    return remap.get(oldId) ?? TERMINAL_NODE_ID;
  };

  const newNodes: GraphNode[] = [];
  // keep trigger node first.
  const trig = graph.nodes.find((nd) => nd.kind === 'trigger');
  if (trig) newNodes.push({ ...trig, y: NODE_Y_START });

  actionNodes.forEach((nd, i) => {
    const cfg = { ...(nd.config || {}) };
    if (nd.type === 'branch') {
      if (Array.isArray(cfg.branches)) {
        cfg.branches = (cfg.branches as BranchEntry[]).map((b) => ({
          ...b,
          target_node_id: newId(b.target_node_id),
        }));
      }
      if (Array.isArray(cfg.split_branches)) {
        cfg.split_branches = (cfg.split_branches as SplitEntry[]).map((s) => ({
          ...s,
          target_node_id: newId(s.target_node_id),
        }));
      }
      if (cfg.default_target_node_id) cfg.default_target_node_id = newId(cfg.default_target_node_id as string);
    } else if (nd.type === 'wait_for_event') {
      if (cfg.resume_target_node_id) cfg.resume_target_node_id = newId(cfg.resume_target_node_id as string);
      if (cfg.timeout_target_node_id) cfg.timeout_target_node_id = newId(cfg.timeout_target_node_id as string);
    }
    newNodes.push({
      ...nd,
      id: actionNodeId(i),
      config: cfg,
      x: NODE_X,
      y: NODE_Y_START + (i + 1) * NODE_Y_STEP,
    });
  });

  // Recompute edges from the renumbered definition for a consistent view.
  const def = graphToDefinition({
    ...graph,
    nodes: newNodes,
  });
  return definitionToGraph({
    ...def,
    id: '',
    active: false,
  });
}

// ---------------------------------------------------------------------------
// Authoring helpers (R2)
// ---------------------------------------------------------------------------

/** Action types that, once ACTIVE, fire a real external send / notification on
 * the next trigger. Activating a definition containing any of these requires an
 * explicit confirm (R2 AC8) — the engine send path is NOT fail-closed. */
export const SEND_BEARING_ACTION_TYPES = new Set([
  'send_message',
  'send_email',
  'notify_operator',
]);

/** Does the graph contain any send-bearing action (drives the activate confirm)? */
export function graphHasSendBearingAction(
  source: WorkflowGraph | Partial<WorkflowDefinition>
): boolean {
  const types: string[] = Array.isArray((source as WorkflowGraph).nodes)
    ? (source as WorkflowGraph).nodes.filter((n) => n.kind === 'action').map((n) => n.type)
    : ((source as WorkflowDefinition).actions || []).map((a) => a.type);
  return types.some((t) => SEND_BEARING_ACTION_TYPES.has(t));
}

/** A fresh empty graph for the "New Workflow" path (a trigger node, no actions). */
export function emptyGraph(name = ''): WorkflowGraph {
  return definitionToGraph({
    id: '',
    name,
    active: false,
    trigger: { type: 'new_lead', config: {} },
    actions: [],
    re_enrollment: { policy: 'once' },
  });
}

/** Default flat config for a freshly-dropped action node of the given type. */
function defaultConfigForType(type: string): Record<string, unknown> {
  switch (type) {
    case 'wait':
      return { delay_minutes: 30 };
    case 'tag':
      return { add_tag_ids: [], remove_tag_ids: [] };
    case 'update_field':
      return { collection: 'contacts', field_path: '', value: '' };
    case 'send_message':
      return { channel: 'sms', content: '' };
    case 'notify_operator':
      return { message: '', channel: 'in_app' };
    case 'enroll_sequence':
      return { sequence_definition_id: '' };
    case 'assign_owner':
      return { collection: 'opportunities', round_robin: [] };
    case 'create_task':
      return { title: '', due_offset_hours: 24 };
    case 'branch':
      return {
        branches: [{ condition: { type: 'tag_present', tag: '' }, target_node_id: TERMINAL_NODE_ID }],
        split_branches: [],
        default_target_node_id: TERMINAL_NODE_ID,
      };
    case 'wait_for_event':
      return {
        event_type: 'inbound_reply',
        timeout_minutes: 1440,
        resume_target_node_id: TERMINAL_NODE_ID,
        timeout_target_node_id: TERMINAL_NODE_ID,
      };
    default:
      return {};
  }
}

/** Append a new action node of `type` to the graph (after `afterNodeId`, or at
 * the end), then renumber for a consistent, in-bounds graph. */
export function addActionNode(
  graph: WorkflowGraph,
  type: string,
  afterNodeId?: string | null
): WorkflowGraph {
  const actionNodes = graph.nodes.filter((nd) => nd.kind === 'action');
  const newNode: GraphNode = {
    id: `new-${Math.random().toString(36).slice(2, 9)}`,
    kind: 'action',
    type,
    config: defaultConfigForType(type),
    x: NODE_X,
    y: 0,
  };

  let insertAt = actionNodes.length;
  if (afterNodeId) {
    const idx = actionNodes.findIndex((nd) => nd.id === afterNodeId);
    if (idx >= 0) insertAt = idx + 1;
  }
  const nextActions = [...actionNodes];
  nextActions.splice(insertAt, 0, newNode);

  const trig = graph.nodes.find((nd) => nd.kind === 'trigger');
  const nextNodes = trig ? [trig, ...nextActions] : nextActions;
  return renumberGraph({ ...graph, nodes: nextNodes });
}

/** Delete an action node, reconnecting predecessor->successor and renumbering so
 * no jump target dangles out-of-bounds (R2 AC6). */
export function deleteActionNode(graph: WorkflowGraph, nodeId: string): WorkflowGraph {
  const nextNodes = graph.nodes.filter((nd) => nd.id !== nodeId);
  return renumberGraph({ ...graph, nodes: nextNodes });
}

/** Replace a node's flat config (R2 — the side-panel writes this). When the
 * trigger node is edited, the graph's top-level `trigger.config` is kept in sync
 * (the trigger node config IS the trigger config). */
export function setNodeConfig(
  graph: WorkflowGraph,
  nodeId: string,
  config: Record<string, unknown>
): WorkflowGraph {
  const nodes = graph.nodes.map((nd) => (nd.id === nodeId ? { ...nd, config } : nd));
  const edited = nodes.find((nd) => nd.id === nodeId);
  let trigger = graph.trigger;
  if (edited && edited.kind === 'trigger') {
    trigger = { type: edited.type, config: { ...config } };
  }
  // Re-derive edges so the canvas reflects the new control flow immediately.
  return renumberGraph({ ...graph, nodes, trigger });
}

/** Change a node's type (trigger or action), resetting config to the type's
 * defaults for action nodes. */
export function setNodeType(
  graph: WorkflowGraph,
  nodeId: string,
  type: string
): WorkflowGraph {
  const nodes = graph.nodes.map((nd) => {
    if (nd.id !== nodeId) return nd;
    if (nd.kind === 'trigger') return { ...nd, type, config: {} };
    return { ...nd, type, config: defaultConfigForType(type) };
  });
  // Update the graph's top-level trigger when the trigger node changed.
  let trigger = graph.trigger;
  const trigNode = nodes.find((nd) => nd.kind === 'trigger');
  if (trigNode) trigger = { type: trigNode.type, config: { ...trigNode.config } };
  return renumberGraph({ ...graph, nodes, trigger });
}

export { nodeIdToIndex };
